type Props = {
  alt: string;
  url: string;
  classes?: string;
  dataTestId?: string;
  useCache?: boolean; // unused; SVG reuse is the browser HTTP cache
  // When set (e.g. "bg-accent"), the SVG is tinted with the given ODS background token.
  // Single-color icons only. Requires an explicit width AND height in `classes`
  // (the tinted wrapper has no intrinsic size).
  maskColorClass?: string;
};

// Returns an <img> that uses a .svg vector as src, or a tinted wrapper when `maskColorClass` is set.
// Vector images scale with higher resolutions without quality loss.
const Image = ({alt, url, classes = '', dataTestId, maskColorClass}: Props): JSX.Element => {
  const src = `${url}.svg`;
  // A tinted wrapper has no intrinsic size, so when tinting via `maskColorClass` we fall back to a
  // default width/height for whichever dimension the caller didn't explicitly provide in `classes`.
  const hasWidthClass = /(?:^|\s)(?:w-|size-)/.test(classes);
  const hasHeightClass = /(?:^|\s)(?:h-|size-)/.test(classes);
  const defaultMaskSizeClasses = maskColorClass
    ? `${hasWidthClass ? '' : 'w-6'} ${hasHeightClass ? '' : 'h-6'}`.trim()
    : '';

  // CSS mask-image requires CORS for cross-origin SVGs. Prod cdn.lifelock.com GET responses
  // often omit Access-Control-Allow-Origin, so masks render blank. Tint via a normal <img>
  // plus a color overlay with mix-blend-mode: lighten (a real MixBlendMode — destination-in
  // is ignored by browsers and left the ODS background as a solid square).
  if (maskColorClass) {
    return (
      <span
        role="img"
        aria-label={alt}
        data-testid={dataTestId}
        className={`${defaultMaskSizeClasses} ${classes} svg-icon svg-icon-tint relative inline-block shrink-0`.trim()}
      >
        <img
          alt=""
          aria-hidden="true"
          src={src}
          className="absolute inset-0 h-full w-full object-contain object-center"
        />
        <span
          aria-hidden="true"
          className={`svg-icon-tint-overlay pointer-events-none absolute inset-0 ${maskColorClass}`}
        />
      </span>
    );
  }

  return <img alt={alt} className={`${classes} svg-icon`} src={src} data-testid={dataTestId} />;
};

export default Image;
